import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { Alert, Card, EmptyState, Spinner, StatusPill, formatSlot } from '../../components/ui';

export default function FamilyDashboard() {
  const { t } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [bookings, setBookings] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([api.get('/api/family/profile'), api.get('/api/family/bookings')])
      .then(([p, b]) => {
        setProfile(p);
        setBookings(b);
      })
      .catch((err) => setError(err.message));
  }, []);

  if (error) return <Alert kind="error">{error}</Alert>;
  if (!profile || !bookings) return <Spinner />;

  const { family, children } = profile;
  const incomplete = !family.profile.addressLine1 || children.length === 0;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t('familyDashboard.hello', { name: family.firstName })}</h1>
          <p className="muted">{family.profile.familyName || t('familyDashboard.yourFamilyPortal')}</p>
        </div>
        <Link className="btn btn-primary" to="/family/request">{t('familyDashboard.requestNanny')}</Link>
      </div>

      {incomplete && (
        <Alert kind="info">
          {t('familyDashboard.completeProfile')}{' '}
          <Link to="/family/profile">{t('familyDashboard.finishProfile')}</Link>.
        </Alert>
      )}

      <div className="stat-row">
        <div className="stat">
          <span className="stat-value">{bookings.upcoming.length}</span>
          <span className="stat-label">{t('familyDashboard.upcomingBookings')}</span>
        </div>
        <div className="stat">
          <span className="stat-value">{bookings.past.length}</span>
          <span className="stat-label">{t('familyDashboard.previousBookings')}</span>
        </div>
        <div className="stat">
          <span className="stat-value">{children.length}</span>
          <span className="stat-label">{t('familyDashboard.childrenOnFile')}</span>
        </div>
      </div>

      <Card
        title={t('familyDashboard.upcomingBookings')}
        actions={<Link className="btn btn-ghost" to="/family/bookings">{t('familyDashboard.viewAll')}</Link>}
      >
        {bookings.upcoming.length ? (
          <ul className="booking-list">
            {bookings.upcoming.slice(0, 5).map((booking) => (
              <li key={booking.id}>
                <div>
                  <strong>{formatSlot(booking)}</strong>
                  <small className="muted">
                    {booking.nanny ? t('familyDashboard.nannyLabel', { name: booking.nanny.name }) : t('familyDashboard.matchingNanny')}
                  </small>
                </div>
                <StatusPill status={booking.status} label={booking.statusLabel} />
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState title={t('familyDashboard.noBookingsYet')}>
            <Link to="/family/request">{t('familyDashboard.requestNanny')}</Link> {t('familyDashboard.requestForDate')}
          </EmptyState>
        )}
      </Card>
    </>
  );
}
